/**
 * Wizard Form 工具函数库
 * 纯静态函数，不依赖 Liquid 变量
 */

// ========== 通用工具函数 ==========

/**
 * 格式化金额（美分转美元）
 * @param {number} cents - 金额（美分）
 * @returns {string} 格式化后的金额字符串
 */
function formatMoney(cents) {
  return "$" + (cents / 100).toFixed(2);
}

/**
 * 自动调整 textarea 高度
 * @param {HTMLTextAreaElement} el - textarea 元素
 */
function autoResizeTextarea(el) {
  const cs = getComputedStyle(el);
  el.style.height = "auto";
  const maxHeight = parseFloat(getComputedStyle(el).maxHeight) || Infinity;
  el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
}

// ========== 图片处理函数 ==========

/**
 * DataURL 转 Blob
 * @param {string} dataUrl - base64 格式的 dataUrl
 * @returns {Blob}
 */
function dataUrlToBlob(dataUrl) {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

/**
 * URL 转 Blob（支持 dataUrl 和 CDN URL）
 * @param {string} url - 图片 URL
 * @returns {Promise<Blob>}
 */
async function urlToBlob(url) {
  if (url.startsWith("data:")) {
    return dataUrlToBlob(url);
  }
  const response = await fetch(url);
  return response.blob();
}

/**
 * 压缩图片并转 base64
 * @param {File} file - 图片文件
 * @param {number} maxWidth - 最大宽度，默认 768
 * @param {number} quality - 压缩质量，默认 0.8
 * @returns {Promise<string>} base64 字符串
 */
function compressImageToBase64(file, maxWidth = 768, quality = 0.8) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // 按比例缩放
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // 转成 base64（带前缀）
        const base64 = canvas.toDataURL("image/jpeg", quality);
        resolve(base64);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ========== 进度动画函数 ==========

/**
 * 启动进度条动画（缓慢增长）+ 动态文案
 * @param {HTMLElement} skeletonGrid - 骨架屏容器
 * @param {HTMLElement} uploadContainer - 上传容器
 */
function startProgressAnimation(skeletonGrid, uploadContainer) {
  const progressTexts = skeletonGrid.querySelectorAll(".progress-text");
  const progressBars = skeletonGrid.querySelectorAll(".progress-bar");
  const loadingMessages = skeletonGrid.querySelectorAll(".loading-message");
  const circumference = 138;
  let progress = 5;

  // 更新 UI
  function updateUI(val) {
    progressTexts.forEach((el) => (el.textContent = Math.floor(val) + "%"));
    progressBars.forEach(
      (bar) => (bar.style.strokeDashoffset = circumference * (1 - val / 100)),
    );
  }

  // 缓慢增长到 85%（约 40 秒）
  const interval = setInterval(() => {
    if (progress < 85) {
      progress += Math.random() * 1.0 + 1.0;
      updateUI(Math.min(progress, 85));
    }
  }, 500);

  // 文案切换
  const messages = [
    { text: "Analyzing your photo...", delay: 0 },
    { text: "Processing details...", delay: 8000 },
    { text: "Creating your look...", delay: 18000 },
    { text: "Almost there...", delay: 30000 },
  ];
  const timeouts = messages.map((m) =>
    setTimeout(
      () => loadingMessages.forEach((el) => (el.textContent = m.text)),
      m.delay,
    ),
  );

  // 存储状态
  skeletonGrid._progress = { value: progress, updateUI, interval };
  uploadContainer._timeouts = timeouts;
}

/**
 * 完成进度动画（快速冲刺到 100%）
 * @param {HTMLElement} skeletonGrid - 骨架屏容器
 * @param {HTMLElement} uploadContainer - 上传容器
 * @returns {Promise} 动画完成后 resolve
 */
function completeProgressAnimation(skeletonGrid, uploadContainer) {
  const state = skeletonGrid._progress;
  if (!state) return Promise.resolve();

  clearInterval(state.interval);
  uploadContainer._timeouts?.forEach((t) => clearTimeout(t));

  // 快速冲刺到 100%
  return new Promise((resolve) => {
    const sprint = setInterval(() => {
      state.value += 5;
      if (state.value >= 100) {
        state.value = 100;
        state.updateUI(100);
        clearInterval(sprint);
        resolve();
      } else {
        state.updateUI(state.value);
      }
    }, 30);
  });
}

/**
 * 停止进度条动画（直接清理，不冲刺）
 * @param {HTMLElement} skeletonGrid - 骨架屏容器
 * @param {HTMLElement} uploadContainer - 上传容器
 */
function stopProgressAnimation(skeletonGrid, uploadContainer) {
  const state = skeletonGrid._progress;
  if (state) clearInterval(state.interval);
  uploadContainer._timeouts?.forEach((t) => clearTimeout(t));
  skeletonGrid
    .querySelectorAll(".loading-message")
    .forEach((el) => (el.textContent = ""));
}

// ========== 图片预览 Tooltip ==========

/**
 * 初始化图片预览 Tooltip
 * @param {HTMLElement} container - 表单容器
 * @param {boolean} supportsHover - 是否支持 hover（桌面端）
 * @returns {{ showTooltip: Function, hideTooltip: Function }} tooltip 控制函数
 */
function initImagePreviewTooltip(container, supportsHover) {
  const tooltip = document.createElement("div");
  tooltip.className = "image-preview-tooltip";
  tooltip.innerHTML = `
    <img class="preview-img" src="" alt="">
    <div class="preview-label"></div>
    <div class="preview-price"></div>
  `;
  container.appendChild(tooltip);

  const tooltipImg = tooltip.querySelector(".preview-img");
  const tooltipLabel = tooltip.querySelector(".preview-label");
  const tooltipPrice = tooltip.querySelector(".preview-price");
  tooltipImg.decoding = "async";
  tooltipImg.loading = "eager";
  let currentHoverItem = null;
  let currentTrigger = null;

  function showTooltip(item, trigger) {
    const previewUrl = item.querySelector("img")?.src || "";
    const value = item.dataset.value;
    const price = item.dataset.price;
    if (!previewUrl) return;

    if (tooltip.dataset.currentUrl !== previewUrl) {
      tooltipImg.src = previewUrl;
      tooltip.dataset.currentUrl = previewUrl;
    }
    tooltipLabel.textContent = value || "";
    if (price && parseInt(price) > 0) {
      tooltipPrice.textContent = "+$" + (parseInt(price) / 100).toFixed(2);
      tooltipPrice.style.display = "block";
    } else {
      tooltipPrice.textContent = "";
      tooltipPrice.style.display = "none";
    }

    currentHoverItem = item;
    currentTrigger = trigger || item;

    tooltip.classList.add("visible");
    updateTooltipPosition();
  }

  function hideTooltip() {
    tooltip.classList.remove("visible");
    currentHoverItem = null;
    currentTrigger = null;
  }

  function updateTooltipPosition() {
    if (!currentTrigger || typeof FloatingUIDOM === "undefined") return;

    FloatingUIDOM.computePosition(currentTrigger, tooltip, {
      placement: "top",
      middleware: [
        FloatingUIDOM.offset(12),
        FloatingUIDOM.flip(),
        FloatingUIDOM.shift({ padding: 8 }),
      ],
    }).then(({ x, y }) => {
      Object.assign(tooltip.style, {
        left: x + "px",
        top: y + "px",
      });
    });
  }

  // 绑定 tooltip 事件
  container
    .querySelectorAll(".radio-pic-grid .option-pic-btn")
    .forEach((btn) => {
      const item = btn.closest(".option-pic-item");
      if (!item) return;
      if (supportsHover) {
        btn.addEventListener("mouseenter", () => showTooltip(item, btn));
        btn.addEventListener("mouseleave", hideTooltip);
      }
    });

  // 移动端点击关闭
  if (!supportsHover) {
    tooltip.addEventListener("click", (e) => {
      e.stopPropagation();
    });
    document.addEventListener("click", (e) => {
      if (tooltip.contains(e.target)) return;
      hideTooltip();
    });
  }

  return { showTooltip, hideTooltip };
}

// ========== Select 下拉组件 ==========

/**
 * 初始化 Select 下拉组件
 * @param {HTMLElement} container - 表单容器
 * @param {Function} calculateTotal - 计算总价的回调函数
 */
function initSelectDropdowns(container, calculateTotal) {
  container.querySelectorAll(".select-container").forEach((selectContainer) => {
    const trigger = selectContainer.querySelector(".select-trigger");
    const dropdown = selectContainer.querySelector(".select-dropdown");
    const valueDisplay = selectContainer.querySelector(".select-value");
    const priceDisplay = selectContainer.querySelector(".select-price");
    const fieldSection = selectContainer.closest(".select-field");
    const customInputWrapper = fieldSection?.querySelector(
      ".custom-input-wrapper",
    );
    const customInput = fieldSection?.querySelector(".custom-input");
    const fieldId = selectContainer.dataset.fieldId;
    const hiddenInput = document.getElementById("prop_" + fieldId);
    const customHiddenInput = document.getElementById(
      "prop_" + fieldId + "_custom",
    );

    let currentSelectedValue = "";

    // 更新隐藏字段值的函数
    function updateSelectHiddenField() {
      if (!hiddenInput) return;
      const customText = customInput?.value?.trim() || "";
      hiddenInput.value = currentSelectedValue;
      if (customHiddenInput) {
        customHiddenInput.value = customText;
      }
      container.dispatchEvent(new CustomEvent("varNameChanged"));
    }

    // 点击触发器 - 展开/收起
    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      container.querySelectorAll(".select-container.open").forEach((other) => {
        if (other !== selectContainer) other.classList.remove("open");
      });
      selectContainer.classList.toggle("open");
    });

    // 点击选项
    dropdown.querySelectorAll(".select-option").forEach((option) => {
      option.addEventListener("click", (e) => {
        e.stopPropagation();
        const value = option.dataset.value;
        const variantId = option.dataset.variant;
        const price = parseInt(option.dataset.price || 0);
        const label = option.querySelector(".option-label").textContent;

        // 更新选中状态
        dropdown
          .querySelectorAll(".select-option")
          .forEach((o) => o.classList.remove("selected"));
        option.classList.add("selected");

        // 更新显示值
        valueDisplay.textContent = label;

        // 更新价格显示
        if (priceDisplay) {
          priceDisplay.textContent =
            price > 0
              ? option.querySelector(".option-price")?.textContent || ""
              : "";
        }

        // 关闭下拉框
        selectContainer.classList.remove("open");

        // 记录选中值
        currentSelectedValue = value;

        // 处理补充信息输入框显示
        if (customInputWrapper) {
          if (variantId) {
            customInputWrapper.style.display = "block";
            if (customInput) autoResizeTextarea(customInput);
            customInput?.focus();
          } else {
            customInputWrapper.style.display = "none";
            if (customInput) customInput.value = "";
          }
        }

        // 更新隐藏字段
        updateSelectHiddenField();

        // 清除错误提示
        const errorMsg = fieldSection?.querySelector(".error-message");
        if (errorMsg) errorMsg.classList.remove("show");

        // 更新价格
        calculateTotal();
      });
    });

    // 补充信息输入框事件
    if (customInput) {
      const maxChars = parseInt(customInput.dataset.maxChars || 0);
      customInput.addEventListener("input", () => {
        if (maxChars > 0 && customInput.value.length > maxChars) {
          customInput.value = customInput.value.slice(0, maxChars);
        }
        updateSelectHiddenField();
      });
    }
  });

  // 点击外部关闭下拉框
  document.addEventListener("click", () => {
    container.querySelectorAll(".select-container.open").forEach((s) => {
      s.classList.remove("open");
    });
  });
}

// ========== 表单提交函数 ==========

/**
 * 收集表单属性
 * @param {HTMLElement} container - 表单容器
 * @returns {Object} 属性对象
 */
function collectFormProperties(container) {
  if (window.Shopify) {
    const properties = {};
    container
      .querySelectorAll('input[type="hidden"][name^="properties["]')
      .forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (match && input.value) {
          properties[match[1]] = input.value;
        }
      });
    return properties;
  }
  // Shopline: 数组格式 [{ Name, Value, Type, Show }]
  const properties = [];
  container
    .querySelectorAll('input[type="hidden"][name^="properties["]')
    .forEach((input) => {
      const match = input.name.match(/^properties\[(.+)\]$/);
      if (match && input.value) {
        properties.push({
          Name: match[1],
          Value: input.value,
          Type: "text",
          Show: true,
        });
      }
    });
  return properties;
}

/**
 * 收集加购商品并提交到 Shopify 购物车
 * @param {HTMLElement} container - 表单容器
 * @param {number} mainProductId - 主产品 ID
 * @param {Object} properties - 主产品属性
 * @returns {Array} 购物车商品列表
 */
function parseVariantId(id) {
  return window.Shopify ? parseInt(id) : String(id);
}

function collectCartItems(container, mainProductId, properties) {
  const itemsToAdd = [
    {
      id: parseVariantId(mainProductId),
      quantity: 1,
      properties: properties,
    },
  ];

  // 收集选中的加购商品（radio, radio-pic, select）
  container.querySelectorAll(".addon-item.selected").forEach((item) => {
    const variantId = item.dataset.variant;
    if (variantId) {
      itemsToAdd.push({
        id: parseVariantId(variantId),
        quantity: 1,
      });
    }
  });

  // 收集额外图片加购产品（第一张免费，从第二张开始）
  container.querySelectorAll(".image-upload-container").forEach((uc) => {
    const extraVariantId = uc.dataset.extraVariantId;
    const selected = uc.querySelectorAll(".result-item.selected").length;
    const extraCount = Math.max(0, selected - 1);

    if (extraVariantId && extraCount > 0) {
      itemsToAdd.push({
        id: parseVariantId(extraVariantId),
        quantity: extraCount,
      });
    }
  });

  return itemsToAdd;
}

/**
 * 获取购物车 API 路由前缀
 * Shopify: window.Shopify.routes.root (如 "/")
 * Shopline: "/api/" 或 window.STARTER?.routes
 * @returns {string}
 */
function getCartRoutePrefix() {
  if (window.Shopify?.routes?.root) {
    return window.Shopify.routes.root;
  }
  return "/";
}

/**
 * 获取购物车 API 端点
 * @param {string} action - 'clear' 或 'add'
 * @returns {string}
 */
function getCartEndpoint(action) {
  const prefix = getCartRoutePrefix();
  // Shopify 使用 cart/clear.js, Shopline 使用 cart/clear
  const suffix = window.Shopify ? ".js" : "";
  return prefix + "cart/" + action + suffix;
}

/**
 * 使用订单号模式提交
 * @param {Object} options - 配置选项
 * @param {string} options.apiHost - API 地址
 * @param {number} options.mainProductId - 主产品 ID
 * @param {Object} options.orderData - 订单数据
 * @param {Array} options.addonItems - 加购商品列表（不含主产品）
 * @returns {Promise}
 */
function submitWithOrderId({ apiHost, mainProductId, orderData, addonItems }) {
  return fetch(apiHost + "/api/forms/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data: orderData }),
  })
    .then((response) => response.json())
    .then((result) => {
      if (!result.orderId) {
        throw new Error("Failed to get order ID");
      }
      return fetch(getCartEndpoint("clear"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }).then(() => result.orderId);
    })
    .then((orderId) => {
      return fetch(getCartEndpoint("add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: parseVariantId(mainProductId),
              quantity: 1,
              properties: window.Shopify
                ? { "Order ID": orderId }
                : [{ Name: "Order ID", Value: orderId, Type: "text", Show: true }],
            },
            ...addonItems.map((item) => ({
              id: parseVariantId(item.id),
              quantity: item.quantity,
            })),
          ],
        }),
      });
    })
    .then((response) => response.json())
    .then((data) => {
      if (data.status === 422 || data.description) {
        throw new Error(data.description || "Error");
      }
      window.location.href = "/checkout";
    });
}

/**
 * 直接提交到 Shopify 购物车
 * @param {Array} items - 购物车商品列表
 * @returns {Promise}
 */
function submitToShopifyCart(items) {
  return fetch(getCartEndpoint("clear"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  })
    .then(() => {
      return fetch(getCartEndpoint("add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      });
    })
    .then((response) => response.json())
    .then((data) => {
      if (data.status === 422 || data.description) {
        throw new Error(data.description || "Error");
      }
      window.location.href = "/checkout";
    });
}
